/**
 * Sticky header component showing currently visible thread names with colored dots.
 * Appears when threads scroll past the viewport top.
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import type { LaneThread, OtherThreadSegment } from "../types";
import { decodeHtmlEntities } from "../utils";

interface StickyThreadHeaderProps {
  stickyLaneThreads: Set<string>;
  stickyOtherThreadSegment: number | null;
  laneThreads: LaneThread[];
  otherThreadSegments: OtherThreadSegment[];
  laneThickness: number;
  laneSpacing: number;
  lanesLeftOffset: number;
  otherThreadsLaneColor: string;
  dateColumnWidth: number;
}

/**
 * Renders the sticky header with thread names and colored lane lines.
 * Only visible when threads have scrolled past the top of the container.
 */
export function StickyThreadHeader({
  stickyLaneThreads,
  stickyOtherThreadSegment,
  laneThreads,
  otherThreadSegments,
  laneThickness,
  laneSpacing,
  lanesLeftOffset,
  otherThreadsLaneColor,
  dateColumnWidth,
}: StickyThreadHeaderProps) {
  // Offset all lane positions by dateColumnWidth to account for left date column
  const effectiveLanesLeftOffset = lanesLeftOffset + dateColumnWidth;

  const hasSticky =
    stickyLaneThreads.size > 0 || stickyOtherThreadSegment !== null;

  if (!hasSticky) return null;

  const stickyLanes = laneThreads.filter((lane) =>
    stickyLaneThreads.has(lane.threadId)
  );

  return (
    <Box
      sx={{
        className: "sticky-lane-headers",
        position: "relative",
        backgroundColor: "white",
        py: 1,
        overflow: "hidden",
        zIndex: 10,
      }}
    >
      {/* Vertical lane lines extending DOWN from each dot to the bottom (dashed) */}
      {stickyLanes.map((lane, idx) => {
        const laneX =
          effectiveLanesLeftOffset + lane.laneIndex * (laneThickness + laneSpacing);
        const rowTop = 8 + idx * 20;
        const lineTop = rowTop + 18;
        return (
          <Box
            className="lane-line"
            key={`line-${lane.threadId}`}
            sx={{
              position: "absolute",
              left: laneX,
              top: lineTop,
              bottom: -8,
              width: laneThickness,
              backgroundImage: `repeating-linear-gradient(to bottom, ${lane.color} 0px, ${lane.color} 4px, transparent 4px, transparent 8px)`,
              zIndex: 0,
            }}
          />
        );
      })}

      {/* Gray lane line for other thread segment (dashed) */}
      {stickyOtherThreadSegment !== null &&
        otherThreadSegments[stickyOtherThreadSegment] &&
        (() => {
          const stickyLaneCount = stickyLanes.length;
          const lineTop = 8 + stickyLaneCount * 20 + 18;
          return (
            <Box
              sx={{
                position: "absolute",
                left:
                  effectiveLanesLeftOffset +
                  laneThreads.length * (laneThickness + laneSpacing),
                top: lineTop,
                bottom: -8,
                width: laneThickness,
                backgroundImage: `repeating-linear-gradient(to bottom, ${otherThreadsLaneColor} 0px, ${otherThreadsLaneColor} 4px, transparent 4px, transparent 8px)`,
                zIndex: 0,
              }}
            />
          );
        })()}

      {/* Thread labels */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          position: "relative",
          zIndex: 1,
        }}
      >
        {stickyLanes.map((lane) => {
          const laneX =
            effectiveLanesLeftOffset + lane.laneIndex * (laneThickness + laneSpacing);
          return (
            <Box
              key={lane.threadId}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                pl: `${laneX}px`,
              }}
            >
              <Box
                sx={{
                  width: laneThickness,
                  height: laneThickness,
                  borderRadius: "50%",
                  backgroundColor: lane.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: lane.color,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "80%",
                }}
              >
                {decodeHtmlEntities(lane.subject)}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "10px",
                  color: "text.secondary",
                }}
              >
                ({lane.messageCount})
              </Typography>
            </Box>
          );
        })}

        {/* Sticky "other thread" segment subject */}
        {stickyOtherThreadSegment !== null &&
          otherThreadSegments[stickyOtherThreadSegment] && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                pl: `${effectiveLanesLeftOffset + laneThreads.length * (laneThickness + laneSpacing)}px`,
              }}
            >
              <Box
                sx={{
                  width: laneThickness,
                  height: laneThickness,
                  borderRadius: "50%",
                  backgroundColor: otherThreadsLaneColor,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: otherThreadsLaneColor,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "80%",
                }}
              >
                {decodeHtmlEntities(
                  otherThreadSegments[stickyOtherThreadSegment].subject
                )}
              </Typography>
            </Box>
          )}
      </Box>

      {/* Bottom gradient fade - using white opacity instead of gray */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 8,
          background:
            "linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.9))",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}
